/**
 * Recipe costing and stock projection.
 *
 * Ingredient costs are held as centavos-per-base-unit with four implied decimal
 * places (a `MicroCost`), because one gram of flour costs a small fraction of a
 * centavo and rounding at the gram would compound into pesos over a sack.
 */
import { addDays, shopDate } from './dates.js';
export const MICRO_SCALE = 10_000;
/** Purchase price for a quantity, converted into a per-base-unit micro cost. */
export function microCostFrom(totalCost, baseQty) {
    if (baseQty === 0)
        return 0;
    return Math.round((totalCost * MICRO_SCALE) / baseQty);
}
/** Cost of consuming `baseQty` of an ingredient, rounded to whole centavos. */
export function costOf(baseQty, microCost) {
    return Math.round((baseQty * microCost) / MICRO_SCALE);
}
/**
 * Weighted-average cost after receiving new stock.
 * Averaging rather than FIFO: a bakery buys the same flour repeatedly at drifting
 * prices, and the operator wants "what does a loaf cost me now", not a layer stack.
 */
export function blendCost(currentQty, currentCost, incomingQty, incomingCost) {
    const totalQty = currentQty + incomingQty;
    if (totalQty <= 0)
        return incomingCost;
    // Stock can go negative when a sale is recorded before its delivery is keyed
    // in. Falling back to the incoming price keeps the average sane.
    if (currentQty <= 0)
        return incomingCost;
    return Math.round((currentQty * currentCost + incomingQty * incomingCost) / totalQty);
}
export function computeRecipeCost(input) {
    const yieldPerBatch = Math.max(input.batchYield, 1);
    const raw = input.lines.map((line) => {
        const ingredient = input.ingredientsById.get(line.ingredientId);
        // Waste is expressed as a percentage of the nominal quantity: 3% waste on
        // 1 000 g means 1 030 g actually leaves the store room.
        const baseQtyPerBatch = Math.round(line.baseQty * (1 + line.wastePercent / 100));
        const microCost = ingredient?.avgCostPerBase ?? 0;
        const costPerBatch = costOf(baseQtyPerBatch, microCost);
        return {
            ingredientId: line.ingredientId,
            ingredientName: ingredient?.name ?? 'Unknown ingredient',
            baseQtyPerBatch,
            costPerBatch,
            costPerUnit: Math.round(costPerBatch / yieldPerBatch),
            share: 0,
            isCostMissing: !ingredient || microCost <= 0,
        };
    });
    const costPerBatch = raw.reduce((sum, l) => sum + l.costPerBatch, 0);
    const lines = raw.map((line) => ({
        ...line,
        share: costPerBatch > 0 ? Math.round((line.costPerBatch / costPerBatch) * 1000) / 10 : 0,
    }));
    return {
        costPerBatch,
        costPerUnit: Math.round(costPerBatch / yieldPerBatch),
        lines,
        isComplete: lines.every((l) => !l.isCostMissing),
    };
}
/**
 * Average daily consumption over the window.
 *
 * Days with no consumption count as zero rather than being skipped: a bakery
 * closed on Sunday genuinely uses no flour that day, and averaging only over
 * baking days would overstate the burn rate and trigger false alarms.
 */
export function burnRate(input) {
    if (input.windowDays <= 0)
        return 0;
    const total = input.dailyConsumption.reduce((sum, d) => sum + d.baseQty, 0);
    return total / input.windowDays;
}
export function alertLevel(qtyOnHand, reorderPoint, daysOfCover, minDays) {
    if (qtyOnHand <= 0)
        return 'out';
    if (daysOfCover !== null && daysOfCover <= 1)
        return 'critical';
    if (reorderPoint > 0 && qtyOnHand <= reorderPoint)
        return 'low';
    if (daysOfCover !== null && daysOfCover <= minDays)
        return 'low';
    return 'ok';
}
/**
 * The signal an operator can act on is not "below threshold" but "you run out on
 * Thursday, and you bake on Friday". Days-of-cover turns a static number into a date.
 */
export function buildStockAlert(input) {
    const { ingredient, dailyBurn } = input;
    const today = input.today ?? shopDate();
    const daysOfCover = dailyBurn > 0 ? ingredient.qtyOnHand / dailyBurn : null;
    const level = alertLevel(ingredient.qtyOnHand, ingredient.reorderPoint, daysOfCover, input.lowStockDaysOfCover);
    // Order enough to refill to the reorder quantity, or a fortnight of cover if
    // no explicit reorder quantity has been set.
    const target = ingredient.reorderQty > 0 ? ingredient.reorderQty : Math.ceil(dailyBurn * 14);
    const suggestedOrderQty = Math.max(target - Math.max(ingredient.qtyOnHand, 0), 0);
    return {
        ingredientId: ingredient.id,
        ingredientName: ingredient.name,
        stockUnit: ingredient.stockUnit,
        qtyOnHand: ingredient.qtyOnHand,
        reorderPoint: ingredient.reorderPoint,
        level,
        dailyBurn,
        daysOfCover: daysOfCover === null ? null : Math.round(daysOfCover * 10) / 10,
        projectedOutOn: daysOfCover === null ? null : addDays(today, Math.floor(daysOfCover)),
        suggestedOrderQty,
        suggestedOrderCost: costOf(suggestedOrderQty, ingredient.avgCostPerBase),
        defaultSupplierId: ingredient.defaultSupplierId,
        defaultSupplierName: input.supplierName,
    };
}
export const ALERT_LEVEL_RANK = {
    out: 0,
    critical: 1,
    low: 2,
    ok: 3,
};
/** Most urgent first, then soonest to run out. */
export function sortAlerts(alerts) {
    return [...alerts].sort((a, b) => {
        const rank = ALERT_LEVEL_RANK[a.level] - ALERT_LEVEL_RANK[b.level];
        if (rank !== 0)
            return rank;
        return (a.daysOfCover ?? Infinity) - (b.daysOfCover ?? Infinity);
    });
}
//# sourceMappingURL=stock.js.map